import { useEffect } from 'react';

interface PageHeadProps {
  title: string;
  description: string;
  canonicalPath?: string;
  ogImage?: string;
}

/**
 * Mutates <title> + <meta> tags client-side on route change.
 *
 * Vite builds the initial HTML with the default title from index.html; when
 * the user clicks a client-side link (or lands on a sub-page after initial
 * load), we swap the tags so social previews + browser tabs match.
 *
 * Follow-on module will add a pre-render / static-HTML step so crawlers see
 * correct tags on first paint. For now this covers user-facing behavior.
 */
export function PageHead({ title, description, canonicalPath, ogImage }: PageHeadProps) {
  useEffect(() => {
    document.title = title;
    setMeta('description', description);
    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:type', 'website', true);
    if (canonicalPath) {
      setMeta('og:url', `https://auctisync.com${canonicalPath}`, true);
      setLink('canonical', `https://auctisync.com${canonicalPath}`);
    }
    if (ogImage) {
      setMeta('og:image', ogImage, true);
      setMeta('twitter:image', ogImage);
    }
    setMeta('twitter:card', 'summary_large_image');
  }, [title, description, canonicalPath, ogImage]);

  return null;
}

function setMeta(key: string, value: string, isProperty = false): void {
  const attr = isProperty ? 'property' : 'name';
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

function setLink(rel: string, href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}
